import { WistorAppCtx, WistorBackendAppCtx } from "../../../../hooks/use-app-ctx"
import { RemoteProxy } from "penpal/dist/penpal"
import { IfcProgressStage, IFrameApi } from "../iframe-api"
import { SparqlManager } from "../../../../helpers/SparqlManager"
import { ProgressUpdate } from "../../../../shared/progress"
import { PublishedDict } from "../../../../redux/pubsub"
import { IFCViewerV4ConfigOptions } from "../config"

type ConversionTarget = "frag" | "ld"

export type FileInfo = {
  type: "ifc" | "frag"
  uri: string
  downloadUrl: string
  filename: string
}

export type ValidConverterDeps = {
  appCtx: WistorBackendAppCtx
  iframeApi: RemoteProxy<IFrameApi>
  sparqlManager: SparqlManager
  appVars: PublishedDict
  widgetConfig: IFCViewerV4ConfigOptions
}

export type ConverterDeps = Omit<ValidConverterDeps, "appCtx" | "iframeApi"> & {
  appCtx: WistorAppCtx
  iframeApi: RemoteProxy<IFrameApi> | null
}

export interface Converter {
  readonly target: ConversionTarget
  readonly queryKey: string
  deps: {
    /**
     * {@link ValidConverterDeps} are required for conversion
     * At time of writing this, we did not represent this in the typing,
     * as could not account for "Rules of Hooks" for React within reasonable time.
     */
    current: ConverterDeps
  }

  findConversionCandidates(urls: string[]): Promise<FileInfo[]>

  convert(
    file: FileInfo,
    progress: (input: ProgressUpdate<IfcProgressStage>) => void
  ): Promise<unknown>
}

export type DepsValidationStatus = "ok" | "wistor-backend-required" | "no-iframe-api"
export function validateDeps(deps: ConverterDeps): DepsValidationStatus {
  if (deps.appCtx.type !== "wistor-backend") {
    return "wistor-backend-required"
  }
  if (deps.iframeApi == null) {
    return "no-iframe-api"
  }
  return "ok"
}

export function narrowDepsOrThrow(deps: ConverterDeps): ValidConverterDeps {
  const result = validateDeps(deps)
  if (result !== "ok") {
    throw new Error(`Cannot perform a conversion: ${result}`)
  }
  return deps as ValidConverterDeps
}

export function toAbsoluteUrl(url: string) {
  return new URL(url, window.location.origin).href
}
